import React from "react";
import { createPortal } from "react-dom";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  X, Mail, Phone, MapPin, User, Car, CalendarClock, Package, DollarSign,
  Wrench, Sparkles, FileText, Tag, ClipboardList, ChevronRight, ChevronDown,
  ExternalLink, Camera, Info, Copy, Check,
} from "lucide-react";
import { jobTypeMeta, installationMeta, visibleJobSubStatuses } from "../lib/jobTypes";
import { summarizeSpec } from "./JobSpecFields";
import { SUB_STATUSES } from "../lib/pipeline";
import { toast } from "sonner";
import JobPhotos from "./JobPhotos";
import { useAuth, usePermission } from "../context/AuthContext";

/**
 * Read-only "everything about this record" popup.
 * Opens for a Lead, Quote, or Job row/card click.
 *
 * Props:
 *   kind    : "lead" | "quote" | "job"
 *   record  : the full document as returned by the API
 *   onClose : () => void
 */
export default function DetailsModal({ kind, record: initialRecord, onClose }) {
  // Local mirror of the record so mutations (per-item status changes, photo
  // uploads) reflect in the open modal without waiting on the parent to
  // refetch. `refetch` pulls the latest snapshot from the API.
  const [record, setRecord] = useState(initialRecord);
  useEffect(() => { setRecord(initialRecord); }, [initialRecord]);
  const refetch = async () => {
    if (!record?.id) return;
    try {
      const { data } = await api.get(`/jobs/${record.id}`);
      setRecord(data);
    } catch (_) { /* modal remains stale but usable */ }
  };
  if (!record) return null;

  const titleMap = { lead: "Lead", quote: "Quote", job: "Job" };
  const isLead = kind === "lead";
  const isQuote = kind === "quote";
  const isJob = kind === "job";

  // Normalize customer fields (leads use name/email/phone/address; quotes/jobs use contact_*)
  const customer = {
    name: record.contact_name || record.name || "",
    email: record.contact_email || record.email || "",
    phone: record.contact_phone || record.phone || "",
    address: record.contact_address || record.address || "",
  };

  const jobTypeValue = record.job_type;
  const jobTypeLabel = jobTypeValue ? jobTypeMeta(jobTypeValue).label : null;

  const isUnquoted = isQuote && (!record.amount || Number(record.amount) <= 0);

  const statusPill = (() => {
    if (isLead) {
      return { new: "pill-info", contacted: "pill-warning", qualified: "pill-success", lost: "pill-error" }[record.status] || "pill-muted";
    }
    if (isQuote) {
      if (isUnquoted) return "pill-error";
      return { draft: "pill-muted", sent: "pill-info", accepted: "pill-success", rejected: "pill-error" }[record.status] || "pill-muted";
    }
    if (isJob) {
      return { scheduled: "pill-info", in_progress: "pill-warning", completed: "pill-success", cancelled: "pill-error" }[record.status] || "pill-muted";
    }
    return "pill-muted";
  })();

  const statusLabel = isQuote && isUnquoted ? "UNQUOTED" : (record.status || "").replace("_", " ");

  const modal = (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
      onClick={onClose}
      data-testid="details-modal-backdrop"
    >
      <div
        className="glass-strong rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid={`details-modal-${kind}`}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">
                <Tag className="w-3 h-3" />
                {titleMap[kind]}
                {jobTypeLabel && <span className="text-slate-600">·</span>}
                {jobTypeLabel && <span className="text-slate-400">{jobTypeLabel}</span>}
              </div>
              <h2 className="font-heading text-3xl text-white font-light tracking-tight truncate">
                {customer.name || "Untitled"}
              </h2>
              {(record.vehicle || record.service) && (
                <div className="text-sm text-slate-400 mt-1">
                  {record.vehicle}{record.vehicle && record.service ? " · " : ""}{record.service}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {record.is_estimate && (
                <span
                  data-testid="details-estimate-badge"
                  title="This quote is a rough estimate — pricing may change once specifics are confirmed."
                  className="pill pill-warning text-[10px] uppercase tracking-wider inline-flex items-center gap-1"
                >
                  <Info className="w-2.5 h-2.5" /> Estimate
                </span>
              )}
              <span className={`pill ${statusPill}`} data-testid="details-status">{statusLabel}</span>
              <button
                type="button"
                data-testid="details-close"
                onClick={onClose}
                className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Amount / Progress banner */}
          {isQuote && (() => {
            const itemsSum = (record.job_items || []).reduce((s, it) => {
              const insts = it.installations || [];
              return s + (insts.length > 0
                ? insts.reduce((ss, inst) => ss + (Number(inst.price) || 0), 0)
                : (Number(it.price) || 0));
            }, 0);
            const overrideVal = record.amount_override != null && record.amount_override !== "" ? Number(record.amount_override) : 0;
            const displayAmount = overrideVal > 0 ? overrideVal : (itemsSum > 0 ? itemsSum : Number(record.amount) || 0);
            const nothingQuoted = displayAmount <= 0;
            return (
              <div className={`rounded-2xl border p-4 flex items-center gap-3 ${nothingQuoted ? "border-red-500/30 bg-red-500/[0.06]" : "border-white/10 bg-white/[0.03]"}`}>
                <DollarSign className={`w-5 h-5 ${nothingQuoted ? "text-red-300" : "text-white"}`} />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    Amount {overrideVal > 0 ? "· Override" : itemsSum > 0 ? "· Auto-sum" : ""}
                  </div>
                  <div className={`text-2xl font-heading ${nothingQuoted ? "text-red-200" : "text-white"}`}>
                    {nothingQuoted ? "Not quoted yet" : `$${displayAmount.toLocaleString()}`}
                  </div>
                  {overrideVal > 0 && itemsSum > 0 && overrideVal !== itemsSum && (
                    <div className="text-[11px] text-slate-500 mt-0.5">Items auto-sum: <span className="line-through">${itemsSum.toLocaleString()}</span></div>
                  )}
                  {nothingQuoted && (
                    <div className="text-[11px] text-red-300 mt-1">
                      Edit this quote and set a price on each item (or an override) before scheduling.
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {isJob && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex justify-between text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                <span>Progress</span>
                <span>{record.progress || 0}%</span>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-400 to-emerald-400" style={{ width: `${record.progress || 0}%` }} />
              </div>
            </div>
          )}

          {/* Customer block (clickable — navigates to Contacts) */}
          <CustomerCard customer={customer} contactId={record.contact_id} onClose={onClose} />

          {/* Multi-item breakdown (new schema) — collapsible per-vehicle sections */}
          {Array.isArray(record.job_items) && record.job_items.length > 0 && (
            <MultiItemBreakdown
              items={record.job_items}
              jobId={record.id}
              kind={kind}
              record={record}
              onRefresh={refetch}
            />
          )}

          {/* Legacy single-item view (only when job_items missing) */}
          {(!Array.isArray(record.job_items) || record.job_items.length === 0) && (record.vehicle || record.service) && (
            <Section icon={Car} title="What's being wrapped">
              {jobTypeLabel && <KV label="Type" value={jobTypeLabel} />}
              {record.vehicle && <KV label="Item" value={record.vehicle} />}
              {record.registration && <KV label="Rego" value={record.registration} />}
              {record.service && <KV label="Service" value={record.service} />}
              {(jobTypeValue === "tinting" || jobTypeValue === "ppf") && summarizeSpec(jobTypeValue, record.spec) && (
                <KV
                  label={jobTypeValue === "tinting" ? "Tinting spec" : "PPF spec"}
                  value={summarizeSpec(jobTypeValue, record.spec)}
                />
              )}
              {record.photo_path && (
                <div className="pt-2">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Customer-supplied photo</div>
                  <LeadPhoto path={record.photo_path} />
                </div>
              )}
            </Section>
          )}

          {/* Photo for jobs that already have job_items (any stage) */}
          {record.photo_path && Array.isArray(record.job_items) && record.job_items.length > 0 && (
            <Section icon={Car} title="Customer-supplied photo">
              <LeadPhoto path={record.photo_path} />
            </Section>
          )}

          {/* Location */}
          {record.location_type && (
            <Section icon={MapPin} title="Install location">
              <KV
                label="Location"
                value={record.location_type === "off_site" ? "Off-site (customer location)" : "On-site (in shop)"}
              />
              {record.location_address && <KV label="Address" value={record.location_address} />}
            </Section>
          )}

          {/* Schedule + installer (jobs) */}
          {isJob && (
            <Section icon={CalendarClock} title="Schedule & crew">
              {record.scheduled_date && (
                <KV
                  label="Dates"
                  value={record.scheduled_end_date && record.scheduled_end_date !== record.scheduled_date
                    ? `${record.scheduled_date} → ${record.scheduled_end_date}`
                    : record.scheduled_date}
                />
              )}
              {record.assigned_to_name && <KV label="Assigned installer" value={record.assigned_to_name} />}
            </Section>
          )}

          {/* Materials */}
          {isJob && Array.isArray(record.materials) && record.materials.length > 0 && (
            <Section icon={Package} title="Materials">
              <div className="flex flex-wrap gap-1.5">
                {record.materials.map((m, i) => (
                  <span key={i} className="pill pill-muted text-[10px]">{m}</span>
                ))}
              </div>
            </Section>
          )}

          {/* Notes */}
          {record.notes && (
            <Section icon={FileText} title="Notes">
              <p className="text-sm text-slate-300 whitespace-pre-wrap">{record.notes}</p>
            </Section>
          )}

          {/* Inspection notes (jobs) */}
          {isJob && record.inspection_notes && (
            <Section icon={ClipboardList} title="Pre-inspection notes">
              <p className="text-sm text-slate-300 whitespace-pre-wrap">{record.inspection_notes}</p>
            </Section>
          )}

          {/* Source (leads only) */}
          {isLead && record.source && record.source !== "manual" && (
            <Section icon={Sparkles} title="Source">
              <KV label="Where it came from" value={record.source} />
              {record.referer && <KV label="Referer" value={record.referer} />}
            </Section>
          )}

          {/* Timestamps */}
          <div className="text-[10px] text-slate-600 flex flex-wrap gap-x-4 gap-y-1 border-t border-white/5 pt-4">
            {record.created_at && <span>Created: {new Date(record.created_at).toLocaleString()}</span>}
            {record.updated_at && <span>Updated: {new Date(record.updated_at).toLocaleString()}</span>}
            <span className="text-slate-700">ID: {record.id}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

/* ------------------------- Small internal helpers ------------------------- */

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">
        <Icon className="w-3 h-3" /> {title}
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-2.5">
        {children}
      </div>
    </div>
  );
}

function KV({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="text-slate-500 min-w-[90px] text-[11px] uppercase tracking-wider flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-slate-200 flex-1 break-words">{value}</div>
    </div>
  );
}

/**
 * Legacy per-item detail block — no longer rendered directly (superseded by
 * <ItemCard />) but kept exported in case any other page consumes it.
 */
function _LegacyJobItemDetail() { return null; }

const CONDITION_LABEL = {
  new: "Brand new / factory",
  excellent: "Excellent",
  good: "Good",
  fair: "Fair — minor imperfections",
  poor: "Poor — needs prep",
};

/**
 * Clickable customer card at the top of the popup — takes the user through
 * to the Contacts tab (highlighted + history opened for that contact).
 */
export function CustomerCard({ customer, contactId, onClose }) {
  const navigate = useNavigate();
  const canNavigate = Boolean(contactId);
  const goToContact = () => {
    if (!canNavigate) return;
    onClose?.();
    navigate(`/contacts?highlight=${encodeURIComponent(contactId)}`);
  };
  return (
    <div
      data-testid="details-customer-card"
      className="w-full rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-2"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
          <User className="w-3 h-3" /> Customer
        </div>
        {canNavigate && (
          <button
            type="button"
            data-testid="details-customer-open-contact"
            onClick={goToContact}
            className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded-full border border-white/10 hover:border-white/30 hover:bg-white/5 transition"
          >
            Open contact <ExternalLink className="w-2.5 h-2.5" />
          </button>
        )}
      </div>
      <div className="text-lg text-white font-medium select-text">{customer.name || "—"}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5 text-xs text-slate-300">
        {customer.email && (
          <div className="flex items-center gap-1 min-w-0">
            <a
              href={`mailto:${customer.email}`}
              data-testid="details-customer-email"
              className="flex items-center gap-1 truncate hover:text-white select-text min-w-0 flex-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Mail className="w-3 h-3 text-slate-500 shrink-0" />
              <span className="truncate">{customer.email}</span>
            </a>
            <CopyButton value={customer.email} testId="details-customer-email-copy" label="email" />
          </div>
        )}
        {customer.phone && (
          <div className="flex items-center gap-1 min-w-0">
            <a
              href={`tel:${customer.phone}`}
              data-testid="details-customer-phone"
              className="flex items-center gap-1 truncate hover:text-white select-text min-w-0 flex-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Phone className="w-3 h-3 text-slate-500 shrink-0" />
              <span className="truncate">{customer.phone}</span>
            </a>
            <CopyButton value={customer.phone} testId="details-customer-phone-copy" label="phone" />
          </div>
        )}
        {customer.address && (
          <span className="flex items-center gap-1 truncate select-text">
            <MapPin className="w-3 h-3 text-slate-500" /> {customer.address}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Tiny "copy to clipboard" pill next to email + phone. Falls back to a
 * temp `<textarea>` on older / non-secure-context browsers where
 * `navigator.clipboard` isn't available. Flashes a checkmark for ~1s
 * so the user has visual confirmation without needing a toast.
 */
function CopyButton({ value, testId, label }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(`${label ? label.charAt(0).toUpperCase() + label.slice(1) : "Value"} copied`);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Couldn't copy — try selecting the text manually");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testId}
      title={`Copy ${label || "value"}`}
      className="p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/5 transition shrink-0"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

/**
 * Renders each job_item as its own collapsible card. Single-item jobs
 * auto-expand; multi-item jobs start collapsed so the receptionist gets a
 * quick summary and can click into whichever vehicle they need.
 */
export function MultiItemBreakdown({ items, jobId, kind, record, onRefresh }) {
  const { user } = useAuth();
  const hidePricing = !usePermission("view_pricing");
  const [openId, setOpenId] = useState(items.length === 1 ? items[0].id : null);
  const total = items.reduce((sum, it) => {
    const insts = it.installations || [];
    return sum + (insts.length > 0
      ? insts.reduce((s, inst) => s + (Number(inst.price) || 0), 0)
      : (Number(it.price) || 0));
  }, 0);
  const hasOverride = record.amount_override != null && record.amount_override !== "" && Number(record.amount_override) > 0;
  const finalAmount = hasOverride ? Number(record.amount_override) : total;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
          <Car className="w-3 h-3" /> Vehicles ({items.length})
        </div>
        {(kind === "quote" || kind === "job") && !hidePricing && (
          <div className="text-xs text-slate-300 flex items-center gap-2">
            {hasOverride && <span className="text-slate-500 line-through">${total.toLocaleString()}</span>}
            <span className="text-white font-semibold">${finalAmount.toLocaleString()}</span>
            <span className="text-[10px] uppercase tracking-widest text-slate-500">
              {hasOverride ? "Override" : "Auto-sum"}
            </span>
          </div>
        )}
      </div>
      {items.map((it, idx) => (
        <ItemCard
          key={it.id || idx}
          item={it}
          index={idx}
          isOpen={openId === it.id}
          onToggle={() => setOpenId(openId === it.id ? null : it.id)}
          jobId={jobId}
          kind={kind}
          record={record}
          onRefresh={onRefresh}
        />
      ))}
    </div>
  );
}

/**
 * One per-vehicle collapsible card. Summary row always visible; expanded
 * body contains type-specific fields, per-item status pipeline, photos, notes.
 */
function ItemCard({ item, index, isOpen, onToggle, jobId, kind, record, onRefresh }) {
  const { user } = useAuth();
  const hidePricing = !usePermission("view_pricing");
  const meta = jobTypeMeta(item.job_type);
  const Icon = meta.icon;
  // Compute installations list — either the new nested array or a synthesized
  // singleton from the legacy top-level installation_type fields.
  const installations = (item.installations && item.installations.length > 0)
    ? item.installations
    : (item.installation_type ? [{
        installation_type: item.installation_type,
        price: item.price || 0,
        wrap_areas: item.wrap_areas || [],
        ppf_areas: item.ppf_areas || [],
        ppf_brand: item.ppf_brand || "",
        design_source: item.design_source || "",
        design_status: item.design_status || "",
        design_notes: item.design_notes || "",
        notes: item.notes || "",
      }] : []);
  const primaryMeta = installations.length > 0 ? installationMeta(installations[0].installation_type) : null;
  const title = [item.year, item.make, item.model].filter(Boolean).join(" ") || meta.label;
  // Sum nested installation prices; fall back to legacy item.price when the
  // record predates the nested schema.
  const price = installations.length > 0
    ? installations.reduce((s, inst) => s + (Number(inst.price) || 0), 0)
    : (Number(item.price) || 0);

  return (
    <div
      data-testid={`item-card-${index}`}
      className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden"
    >
      {/* Always-visible summary row */}
      <button
        type="button"
        data-testid={`item-card-toggle-${index}`}
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.04]"
      >
        <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white shrink-0">
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">Item {index + 1} · {meta.label}</span>
            {installations.length === 1 && primaryMeta && (
              <span className="pill pill-muted text-[9px]" data-testid={`item-installation-pill-${index}`}>
                {primaryMeta.label}
              </span>
            )}
            {installations.length > 1 && (
              <span className="pill pill-info text-[9px]" data-testid={`item-installation-pill-${index}`}>
                {installations.length} installations
              </span>
            )}
            {item.sub_status && (
              <span className="pill pill-info text-[9px]">{item.sub_status.replace(/_/g, " ")}</span>
            )}
          </div>
          <div className="text-sm text-white truncate mt-0.5">{title}</div>
        </div>
        <div className="text-right shrink-0">
          {price > 0 && !hidePricing && <div className="text-sm text-white font-medium">${price.toLocaleString()}</div>}
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            {isOpen ? "Hide" : "Details"}
          </div>
        </div>
        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>

      {isOpen && (
        <div className="border-t border-white/5 p-4 space-y-3 bg-black/20" data-testid={`item-card-body-${index}`}>
          <ItemFieldsView item={item} kind={kind} jobId={jobId} onRefresh={onRefresh} />
          {kind === "job" && (
            <ItemStatusPipeline jobId={jobId} item={item} index={index} onRefresh={onRefresh} />
          )}
          {kind === "job" && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                <Camera className="w-3 h-3" /> Photos for this vehicle
              </div>
              <JobPhotos job={record} itemId={item.id} onUpdate={onRefresh} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Read-only per-item field grid (year/make/model, condition, areas, ppf brand, notes). */
function ItemFieldsView({ item, kind, jobId, onRefresh }) {
  const { user } = useAuth();
  const hidePricing = !usePermission("view_pricing");
  const installations = (item.installations && item.installations.length > 0)
    ? item.installations
    : (item.installation_type ? [{
        installation_type: item.installation_type,
        price: item.price || 0,
        wrap_areas: item.wrap_areas || [],
        ppf_areas: item.ppf_areas || [],
        ppf_brand: item.ppf_brand || "",
        notes: item.notes || "",
        design_source: item.design_source || "",
        design_status: item.design_status || "",
        design_notes: item.design_notes || "",
      }] : []);
  return (
    <>
      {(item.year || item.make || item.model) && (
        <KV label="Vehicle" value={[item.year, item.make, item.model].filter(Boolean).join(" ")} />
      )}
      {item.condition && <KV label="Condition" value={CONDITION_LABEL[item.condition] || item.condition} />}
      {item.body_kit && <KV label="Body kit" value={item.body_kit_notes || "Yes"} />}

      {/* Each installation renders as its own sub-block */}
      {installations.map((inst, idx) => {
        const meta = installationMeta(inst.installation_type);
        return (
          <div
            key={inst.id || idx}
            data-testid={`installation-view-${idx}`}
            className="rounded-xl bg-white/[0.02] border border-white/5 p-3 space-y-2"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Installation {idx + 1}</div>
                <div className="text-sm text-white">
                  {meta?.label || <span className="text-slate-500 italic">Not set</span>}
                </div>
              </div>
              {Number(inst.price) > 0 && !hidePricing && (
                <div className="text-sm text-white font-medium">${Number(inst.price).toLocaleString()}</div>
              )}
            </div>
            {Array.isArray(inst.wrap_areas) && inst.wrap_areas.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Wrap areas</div>
                <div className="flex flex-wrap gap-1.5">
                  {inst.wrap_areas.map((a) => <span key={a} className="pill pill-muted text-[10px]">{a}</span>)}
                </div>
              </div>
            )}
            {Array.isArray(inst.ppf_areas) && inst.ppf_areas.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">PPF areas</div>
                <div className="flex flex-wrap gap-1.5">
                  {inst.ppf_areas.map((a) => <span key={a} className="pill pill-muted text-[10px]">{a}</span>)}
                </div>
              </div>
            )}
            {inst.ppf_brand && <KV label="PPF brand" value={inst.ppf_brand} />}
            {inst.material && (
              <MaterialReadyRow
                material={inst.material}
                materialReady={!!inst.material_ready}
                materialReadyBy={inst.material_ready_by || ""}
                canToggle={kind === "job"}
                jobId={jobId}
                itemId={item.id}
                installationId={inst.id}
                onRefresh={onRefresh}
                testIdSuffix={`${idx}`}
              />
            )}
            {inst.design_source && (
              <KV label="Design" value={
                inst.design_source === "provided" ? "Customer provided"
                : `In-house · ${inst.design_status?.replace(/_/g, " ") || ""}`
              } />
            )}
            {inst.design_notes && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Design notes</div>
                <p className="text-sm text-slate-300 whitespace-pre-wrap">{inst.design_notes}</p>
              </div>
            )}
            {inst.notes && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Notes</div>
                <p className="text-sm text-slate-300 whitespace-pre-wrap">{inst.notes}</p>
              </div>
            )}
          </div>
        );
      })}

      {installations.length === 0 && item.notes && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Notes</div>
          <p className="text-sm text-slate-300 whitespace-pre-wrap">{item.notes}</p>
        </div>
      )}
    </>
  );
}

/**
 * Material row with an in-place "material ready / needs to be ordered"
 * tickbox. Admins/office/installers assigned to the job can flip the
 * flag so the workshop can tell at a glance which installs still need
 * stock pulled or ordered. Read-only (just shows the material name)
 * when `canToggle` is false (e.g. quote view).
 */
function MaterialReadyRow({
  material, materialReady, materialReadyBy,
  canToggle, jobId, itemId, installationId, onRefresh, testIdSuffix,
}) {
  const [ready, setReady] = useState(!!materialReady);
  const [saving, setSaving] = useState(false);
  // Keep local state in sync when the parent refetches the record
  useEffect(() => { setReady(!!materialReady); }, [materialReady]);

  const toggle = async () => {
    if (!canToggle || saving) return;
    const next = !ready;
    setReady(next);
    setSaving(true);
    try {
      await api.patch(
        `/jobs/${jobId}/items/${itemId}/installations/${installationId}/material-ready`,
        { ready: next },
      );
      toast.success(next ? "Marked material ready" : "Marked material to be ordered");
      onRefresh?.();
    } catch (e) {
      setReady(!next); // rollback on error
      toast.error(e?.response?.data?.detail || "Couldn't update material status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`material-row-${testIdSuffix}`}
      className="flex items-start gap-3 text-sm"
    >
      <div className="text-slate-500 min-w-[90px] text-[11px] uppercase tracking-wider pt-0.5">Material</div>
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="text-slate-200 break-words">{material}</div>
        {canToggle ? (
          <button
            type="button"
            onClick={toggle}
            disabled={saving}
            data-testid={`material-ready-toggle-${testIdSuffix}`}
            className={[
              "inline-flex items-center gap-2 text-[11px] rounded-full px-2.5 py-1 border transition",
              ready
                ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/20"
                : "bg-amber-500/10 border-amber-400/40 text-amber-200 hover:bg-amber-500/15",
              saving ? "opacity-60 cursor-wait" : "cursor-pointer",
            ].join(" ")}
          >
            <span
              aria-hidden="true"
              className={[
                "w-4 h-4 rounded border flex items-center justify-center transition shrink-0",
                ready
                  ? "bg-emerald-500 border-emerald-400 text-black"
                  : "bg-transparent border-amber-400/50 text-transparent",
              ].join(" ")}
            >
              <Check className="w-3 h-3" strokeWidth={3} />
            </span>
            <span className="uppercase tracking-wider">
              {ready ? "Material ready" : "Needs ordering"}
            </span>
            {ready && materialReadyBy && (
              <span className="text-emerald-200/60 normal-case tracking-normal">· {materialReadyBy}</span>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}


/**
 * Compact stepper — one dot per sub-status stage; clicking a stage moves the
 * item to that sub_status. Independent from sibling items in the same job.
 */
function ItemStatusPipeline({ jobId, item, index, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const allStages = SUB_STATUSES.job || [];
  // Filter using the same dynamic-stage rules the parent StatusPipeline uses,
  // scoped to just THIS item's installations (a wash-down-only detailer
  // shouldn't see design gates that belong to another vehicle on the job).
  const stages = (() => {
    const fakeJob = { job_items: [item] };
    const visible = new Set(visibleJobSubStatuses(fakeJob));
    if (item.sub_status) visible.add(item.sub_status);
    return allStages.filter((s) => visible.has(s.value));
  })();
  const current = item.sub_status;
  const currentIdx = stages.findIndex((s) => s.value === current);

  const move = async (target) => {
    if (busy || target === current) return;
    setBusy(true);
    try {
      await api.put(`/jobs/${jobId}/items/${item.id}/sub-status`, { sub_status: target });
      toast.success(`Item ${index + 1} moved to ${target.replace(/_/g, " ")}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`item-status-pipeline-${index}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Item status</div>
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {stages.map((s, idx) => {
          const StageIcon = s.icon;
          const isDone = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          return (
            <button
              key={s.value}
              type="button"
              disabled={busy}
              onClick={() => move(s.value)}
              data-testid={`item-status-stage-${index}-${s.value}`}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] whitespace-nowrap transition shrink-0 ${
                isCurrent
                  ? "bg-emerald-500/15 text-emerald-200 border border-emerald-400/40"
                  : isDone
                    ? "bg-white/[0.06] text-slate-300 border border-white/10"
                    : "bg-transparent text-slate-500 border border-white/5 hover:text-slate-300 hover:bg-white/[0.04]"
              }`}
              title={`Move to ${s.label}`}
            >
              <StageIcon className="w-2.5 h-2.5" />
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Fetches a lead photo through the authenticated /api/files endpoint
 * and renders it as a thumbnail; clicking opens a fullscreen lightbox.
 */
function LeadPhoto({ path, size = "md" }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    let alive = true;
    let currentUrl = null;
    (async () => {
      try {
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        if (!alive) return;
        currentUrl = URL.createObjectURL(res.data);
        setBlobUrl(currentUrl);
      } catch {
        setBlobUrl(null);
      }
    })();
    return () => {
      alive = false;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [path]);

  const dim = size === "sm" ? "w-16 h-16" : "w-40 h-28";
  if (!blobUrl) {
    return <div className={`${dim} rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-center text-[11px] text-slate-500`}>Loading…</div>;
  }
  return (
    <>
      <button
        type="button"
        data-testid="lead-photo-thumb"
        onClick={() => setZoom(true)}
        className="block rounded-xl overflow-hidden border border-white/10 hover:border-white/30 transition"
      >
        <img src={blobUrl} alt="Customer vehicle" className={`${dim} object-cover`} />
      </button>
      {zoom && createPortal(
        <div
          className="fixed inset-0 z-[75] bg-black/90 flex items-center justify-center p-6"
          onClick={() => setZoom(false)}
          data-testid="lead-photo-lightbox"
        >
          <img src={blobUrl} alt="Customer vehicle" className="max-w-full max-h-full rounded-xl" />
        </div>,
        document.body
      )}
    </>
  );
}


// Re-export for consumers (JobsPage, QuotesPage) that want to render the
// same customer-supplied photo inline on their cards.
export { LeadPhoto as CustomerVehiclePhoto };
